// @flow

/**
 * Thread Download Utilities
 * Prepares chat thread content for download (PDF/DOCX)
 */

import type { ChatMessage } from '../types';
import { formatDownloadTimestampForFilename } from './downloadHeadings';

/**
 * Filter messages for download - excludes intro messages
 * @param {ChatMessage[]} messages - All chat messages
 * @returns {ChatMessage[]} Messages to include in download
 */
export const filterMessagesForDownload = (messages: ChatMessage[]): ChatMessage[] => {
  return messages.filter((msg) => {
    // Exclude intro messages
    if (msg.role === 'agent' && msg.isIntro) {
      return false;
    }
    // Include messages with text content
    if (msg.role === 'user') {
      return Boolean(msg.text && msg.text.trim());
    }
    if (msg.role === 'agent') {
      return Boolean(msg.text && msg.text.trim());
    }
    return false;
  });
};

/**
 * Format a single message for download
 * User messages are prefixed with bold "User: " and the user's text is italic
 * Agent messages are not prefixed
 * @param {ChatMessage} message - The message to format
 * @returns {string} Formatted message text
 */
export const formatMessageForDownload = (message: ChatMessage): string => {
  const text = message.text || '';
  
  if (message.role === 'user') {
    // Bold "User:" followed by italic message text: **User:** *text*
    return `**User:** *${text}*`;
  }
  
  // Agent messages: return text as-is (no prefix)
  return text;
};

/**
 * Combine multiple messages into a single markdown string
 * Messages are separated by double line breaks
 * @param {ChatMessage[]} messages - Messages to combine
 * @returns {string} Combined markdown content
 */
export const combineMessagesForDownload = (messages: ChatMessage[]): string => {
  const filtered = filterMessagesForDownload(messages);
  
  return filtered
    .map(formatMessageForDownload)
    .join('\n\n');
};

/**
 * Generate filename for thread download
 * @param {string} extension - File extension (e.g., 'pdf', 'docx')
 * @returns {string} Filename like 'chat-conversation-2026-01-07.pdf'
 */
export const generateThreadFilename = (extension: string): string => {
  const ts = formatDownloadTimestampForFilename(new Date());
  return `Aura Vision Conversation ${ts}.${extension}`;
};

/**
 * Check if thread has downloadable content
 * Returns true if there are non-intro messages with text
 * @param {ChatMessage[]} messages - All chat messages
 * @returns {boolean} True if there is content to download
 */
export const hasDownloadableContent = (messages: ChatMessage[]): boolean => {
  const filtered = filterMessagesForDownload(messages);
  return filtered.length > 0;
};
